import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  CircularProgress,
  Chip,
  Typography,
} from "@mui/material";
import { Edit as EditIcon, ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import useApiCall from "../../hooks/useApiCall";
import { usePageMeta } from "../../contexts/PageMetaContext";
import { INDIGO, SLATE } from "../../constants/Colors";

export default function BusinessUnitDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const currentApp = useSelector((state) => state?.apps?.selectedApp);
  const tokens = useSelector((state) => state?.tokens.value);
  const { makeRequest } = useApiCall();
  const { setPageMeta } = usePageMeta();

  const [businessUnit, setBusinessUnit] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    return () => setPageMeta({});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!id || !currentApp?.id) return;
    setLoading(true);
    makeRequest(`/v1/apps/${currentApp.id}/business_units/${id}`, { method: "GET", authTokens: tokens })
      .then((data) => {
        const unit = data?.business_unit;
        if (unit) {
          setBusinessUnit(unit);
          setPageMeta({ itemName: unit.name });
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id, currentApp?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", pt: 10 }}>
        <CircularProgress size={28} sx={{ color: INDIGO[500] }} />
      </Box>
    );
  }

  if (!businessUnit) {
    return (
      <Box sx={{ textAlign: "center", py: 10 }}>
        <Typography sx={{ fontSize: "1rem", color: SLATE[500] }}>Business unit not found.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", maxWidth: 720 }}>
      <Box
        sx={{
          bgcolor: "#ffffff",
          border: `1px solid ${SLATE[200]}`,
          borderRadius: "16px",
          p: 3,
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {businessUnit.logo && (
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "12px",
                  border: `1px solid ${SLATE[200]}`,
                  bgcolor: SLATE[50],
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  flexShrink: 0,
                }}
              >
                <img src={businessUnit.logo} alt={businessUnit.name} style={{ width: 44, height: 44, objectFit: "contain" }} />
              </Box>
            )}
            <Box>
              <Typography sx={{ fontSize: "1.125rem", fontWeight: 700, color: SLATE[900] }}>
                {businessUnit.name}
              </Typography>
              <Typography sx={{ fontSize: "0.8125rem", color: SLATE[500] }}>
                {businessUnit.unit_code}
              </Typography>
            </Box>
          </Box>
          <Button
            variant="outlined"
            startIcon={<EditIcon sx={{ fontSize: "1rem" }} />}
            size="small"
            onClick={() => navigate(`/${currentApp.url_code}/business-units/${id}/edit`)}
            sx={{ textTransform: "none", borderColor: SLATE[300], color: SLATE[700], "&:hover": { borderColor: SLATE[400], bgcolor: SLATE[50] } }}
          >
            Edit
          </Button>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {businessUnit.metadata?.description && (
            <Box>
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: SLATE[400], textTransform: "uppercase", letterSpacing: "0.05em", mb: "4px" }}>Description</Typography>
              <Typography sx={{ fontSize: "0.875rem", color: SLATE[700] }}>{businessUnit.metadata.description}</Typography>
            </Box>
          )}
          {businessUnit.url && (
            <Box>
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: SLATE[400], textTransform: "uppercase", letterSpacing: "0.05em", mb: "4px" }}>Website</Typography>
              <Typography sx={{ fontSize: "0.875rem" }}>
                <a href={businessUnit.url} target="_blank" rel="noopener noreferrer" style={{ color: INDIGO[600] }}>{businessUnit.url}</a>
              </Typography>
            </Box>
          )}
          <Box>
            <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: SLATE[400], textTransform: "uppercase", letterSpacing: "0.05em", mb: "4px" }}>Status</Typography>
            <Chip
              label={businessUnit.status}
              size="small"
              sx={{
                bgcolor: businessUnit.status === "active" ? "#dcfce7" : SLATE[100],
                color: businessUnit.status === "active" ? "#166534" : SLATE[600],
                fontSize: "0.75rem",
                height: 22,
                borderRadius: "6px",
              }}
            />
          </Box>
          <Box>
            <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: SLATE[400], textTransform: "uppercase", letterSpacing: "0.05em", mb: "4px" }}>Agents</Typography>
            <Typography sx={{ fontSize: "0.875rem", color: SLATE[700] }}>
              {businessUnit.agents_count ?? 0} {businessUnit.agents_count === 1 ? "agent" : "agents"}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ mt: 3, pt: 2.5, borderTop: `1px solid ${SLATE[100]}` }}>
          <Button
            startIcon={<ArrowBackIcon sx={{ fontSize: "1rem" }} />}
            size="small"
            onClick={() => navigate(`/${currentApp.url_code}/business-units`)}
            sx={{ textTransform: "none", color: SLATE[600], fontSize: "0.8125rem", "&:hover": { bgcolor: SLATE[50] } }}
          >
            Back to Business Units
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
